'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import {
  Configure,
  useClearRefinements,
  useCurrentRefinements,
  usePagination,
  useSortBy,
  useStats
} from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import { ProductFilter, ProductFilterModal } from 'components/atomic/atoms';
import { CollectionsListSidebar } from 'components/atomic/molecules';
import { BottomModal, CloseIcon, FilterModal } from 'components/atomic/nuclei';
import {
  HeaderOrganism,
  ProductListOrganism
} from 'components/atomic/organism';
import { staticMediaStoreBaseURL } from 'config/common';
import searchAPIConfig from 'config/searchAPI';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import createClient from 'lib/instantSearchClient';
import { getCollectionBySlug } from 'services/collection.service';
import { HOST_PORTAL_FQDN } from 'services/connections.service';
import { getAuthToken } from 'services/identity.service';

const INDEX_NAME = 'products_weightage_desc';

const SORT_ITEMS = [
  { value: 'products_weightage_desc', label: 'Relevance' },
  { value: 'products_price_asc', label: 'Price: Low to High' },
  { value: 'products_price_desc', label: 'Price: High to Low' }
];

const defaultPriceRange = {
  max_price: { value: 0 },
  min_price: { value: 2000 }
};

function SelectedFilterItem({ label, onRemove }) {
  return (
    <li>
      <label className='h-9 flex justify-between items-center border border-platinum cursor-pointer px-4 rounded-md min-w-30 hover:bg-ghost-white'>
        <span className='text-dim-gray font-medium text-sm leading-17px capitalize me-3 select-none'>
          {label}
        </span>
        <CloseIcon
          onClick={onRemove}
          width={16}
          height={16}
        />
      </label>
    </li>
  );
}

function buildDisplayRefinements(activeRefinements, removeRefinement) {
  const result = [];
  for (const item of activeRefinements) {
    if (item.attribute === 'price.sortPrice') {
      const min = item.refinements.find((r) => r.label.startsWith('≥'));
      const max = item.refinements.find((r) => r.label.startsWith('≤'));
      const minVal = min?.label.replace(/≥\s*/, '');
      const maxVal = max?.label.replace(/≤\s*/, '');
      const priceKey = `${minVal ? 'min' : ''}_${maxVal ? 'max' : ''}`;
      const priceLabel = {
        min_max: `AED ${minVal} - AED ${maxVal}`,
        min_: `From AED ${minVal}`,
        _max: `Up to AED ${maxVal}`,
        _: item.refinements.map((r) => r.label).join(' - ')
      };
      const label = priceLabel[priceKey];
      result.push({
        key: 'price.sortPrice',
        label,
        onRemove: () => item.refinements.forEach((r) => removeRefinement(r))
      });
    } else if (item.attribute === 'isInstantPrice') {
      result.push({
        key: 'isInstantPrice',
        label: 'Instant Price Products',
        onRemove: () => item.refinements.forEach((r) => removeRefinement(r))
      });
    } else {
      item.refinements.forEach((r) => {
        result.push({
          key: `${r.attribute}-${r.label}`,
          label: r.label,
          onRemove: () => removeRefinement(r)
        });
      });
    }
  }
  return result;
}

const CollectionDetailsContent = ({
  collectionName,
  isMobile,
  isFiltering,
  setIsFiltering,
  isSorting,
  setIsSorting,
  openTab,
  setOpenTab,
  priceRange,
  handleBackButtonClick,
  updateCurrentUrl,
  onSortClick
}) => {
  const { nbHits } = useStats();
  const { currentRefinement: currentSort, refine: refineSort } = useSortBy({
    items: SORT_ITEMS
  });
  const { items: activeRefinements, refine: removeRefinement } =
    useCurrentRefinements();
  const { refine: clearAllRefinements } = useClearRefinements();
  const {
    currentRefinement: currentPage,
    nbPages,
    isFirstPage,
    isLastPage,
    refine: refinePage
  } = usePagination();

  const safeNbPages = nbPages || 0;
  const padding = 3;
  const pageStart = Math.max(0, currentPage - padding);
  const pageEnd = Math.min(safeNbPages - 1, currentPage + padding);
  const pages = Array.from(
    { length: Math.max(0, pageEnd - pageStart + 1) },
    (_, i) => pageStart + i
  );

  const allRefinements = buildDisplayRefinements(
    activeRefinements,
    removeRefinement
  );

  const renderProductsCount = () => (
    <div
      className={`${
        isMobile ? 'flex items-center my-2.5 px-5' : 'justify-between md:px-1'
      }`}
    >
      <h1 className={`inline-block ${isMobile ? 'truncate pr-1' : ''}`}>
        <span className='font-medium text-base font-Montserrat text-nero leading-5'>
          {collectionName}
        </span>
      </h1>
      <span className='flex-shrink-0 font-normal text-sm font-Montserrat text-dim-gray leading-5'>
        {' - '}
        {nbHits}
        {' products'}
      </span>
    </div>
  );

  const renderResetFilter = () =>
    allRefinements.length > 0 ? (
      <div
        className='ms-4'
        onClick={clearAllRefinements}
      >
        <label className='h-9 flex justify-center items-center border border-platinum cursor-pointer px-4 rounded-md min-w-23 hover:bg-ghost-white'>
          <span className='text-coral font-medium text-sm leading-17px capitalize select-none'>
            {' '}
            Clear All{' '}
          </span>
        </label>
      </div>
    ) : null;

  const renderMobilePagination = () => {
    if (isFirstPage && isLastPage) return null;
    return (
      <div className='px-5 py-5 mb-11 font-Montserrat flex items-center justify-center'>
        {!isFirstPage && (
          <div
            onClick={() => {
              refinePage(currentPage - 1);
              window.scrollTo(0, 0);
            }}
            className='text-cinnabar border border-gray w-1/2 text-sm leading-17px rounded-md flex justify-center items-center cursor-pointer h-9'
          >
            <Image
              src={`${staticMediaStoreBaseURL}/icons/prev-arrow.svg`}
              alt='Previous'
              className='me-2'
              height={12}
              width={12}
            />
            <span className='ms-2'> Previous </span>
          </div>
        )}
        {!isLastPage && (
          <div
            onClick={() => {
              refinePage(currentPage + 1);
              window.scrollTo(0, 0);
            }}
            className='text-cinnabar border border-gray ms-5 w-1/2 text-sm leading-17px rounded-md flex justify-center items-center cursor-pointer h-9'
          >
            <span className='me-2'> Next </span>
            <Image
              src={`${staticMediaStoreBaseURL}/icons/next-arrow.svg`}
              alt='Next'
              className='ms-2'
              height={12}
              width={12}
            />
          </div>
        )}
      </div>
    );
  };

  const renderDesktopPagination = () => {
    if (safeNbPages <= 1) return null;
    return (
      <div className='mt-6 py-4 border-t border-gray flex items-center justify-center font-Montserrat font-medium flex-row'>
        <div
          onClick={() => {
            if (!isFirstPage) {
              refinePage(currentPage - 1);
              window.scrollTo(0, 0);
            }
          }}
          className={`${
            isFirstPage ? 'text-dim-gray' : 'text-cinnabar border border-gray'
          } me-8 text-sm leading-17px h-9 rounded-md inline-flex justify-center items-center px-4 cursor-pointer`}
        >
          <div className='pb-0.2 px-1'>
            <Image
              src={`${staticMediaStoreBaseURL}${
                isFirstPage
                  ? '/icons/left-grey-arrow.svg'
                  : '/icons/prev-arrow.svg'
              }`}
              alt='previous'
              height={0}
              width={0}
              style={{ height: '13px', width: '13px' }}
              className='me-2'
            />
          </div>
          <span> Previous </span>
        </div>
        {pages.map((page) => (
          <div
            key={page}
            onClick={() => {
              refinePage(page);
              window.scrollTo(0, 0);
            }}
            className={`${page === 0 ? 'ms-0' : 'ms-4'} w-full ${
              page === currentPage ? 'text-white bg-cinnabar' : 'text-nero'
            } text-base hover:text-white leading-5 h-7 min-w-6 max-w-6 inline-flex justify-center items-center hover:bg-cinnabar cursor-pointer rounded-sm`}
          >
            <span> {page + 1} </span>
          </div>
        ))}
        <div
          onClick={() => {
            if (!isLastPage) {
              refinePage(currentPage + 1);
              window.scrollTo(0, 0);
            }
          }}
          className={`${
            isLastPage ? 'text-dim-gray' : 'text-cinnabar border border-gray'
          } ms-8 text-sm leading-17px h-9 rounded-md inline-flex justify-center items-center px-4 cursor-pointer`}
        >
          <span> Next </span>
          <div className='pb-0.2 px-1'>
            <Image
              src={`${staticMediaStoreBaseURL}${
                isLastPage
                  ? '/icons/next-arrow-gray.svg'
                  : '/icons/next-arrow.svg'
              }`}
              alt='next'
              height={0}
              width={0}
              style={{ height: '13px', width: '13px' }}
              className='ms-2'
            />
          </div>
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        {renderProductsCount()}
        <ProductListOrganism />
        {renderMobilePagination()}
        <div className='fixed w-full bottom-0'>
          <ProductFilter
            sortOrderShowHideHandler={() => setIsSorting(true)}
            filterByShowHideHandler={() => setIsFiltering(true)}
            filterCount={activeRefinements.length}
          />
        </div>
        <div className={`${!isSorting ? 'hidden' : ''}`}>
          <BottomModal
            paramData={{
              modalHeight: 'min-h-43.5',
              label: 'Sort By',
              setCloseModal: setIsSorting,
              showHeader: true,
              handler: onSortClick
            }}
          >
            <div
              className='flex flex-col px-5 text-matterhorn'
              onClick={onSortClick}
            >
              {SORT_ITEMS.map((item) => (
                <div
                  key={item.value}
                  className={`py-3 cursor-pointer ${
                    currentSort === item.value
                      ? 'text-cinnabar font-semibold'
                      : ''
                  }`}
                  onClick={() => refineSort(item.value)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </BottomModal>
        </div>
        <div className={`${!isFiltering ? 'hidden' : ''}`}>
          <FilterModal
            paramData={{
              modalHeight: 'min-h-full',
              setCloseModal: setIsFiltering,
              showHeader: true,
              handler: () => setIsFiltering(false),
              handleBackButtonClick
            }}
          >
            <ProductFilterModal
              setCloseModal={setIsFiltering}
              updateCurrentUrl={updateCurrentUrl}
              openTab={openTab}
              setOpenTab={setOpenTab}
              priceRange={priceRange}
            />
          </FilterModal>
        </div>
      </>
    );
  }

  return (
    <div className='flex md:px-20 mt-6'>
      <CollectionsListSidebar priceRange={priceRange} />
      <div className='w-full'>
        {renderProductsCount()}
        <div className='flex items-center mt-4 space-x-7 list-none text-nero font-medium text-sm font-Montserrat mb-5 ms-1'>
          <div>Sort By</div>
          <div className='flex items-center space-x-5'>
            {SORT_ITEMS.map((item) => (
              <button
                key={item.value}
                type='button'
                className={`cursor-pointer ${
                  currentSort === item.value ? 'text-cinnabar' : 'text-nero'
                }`}
                onClick={() => refineSort(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className='flex'>
          <ul className='ms-1 flex list-none space-x-4 mb-4'>
            {allRefinements.map((r) => (
              <SelectedFilterItem
                key={r.key}
                label={r.label}
                onRemove={r.onRemove}
              />
            ))}
          </ul>
          {renderResetFilter()}
        </div>
        <ProductListOrganism />
        {renderDesktopPagination()}
        {isFiltering && (
          <FilterModal
            paramData={{
              modalHeight: 'min-h-full',
              setCloseModal: setIsFiltering,
              showHeader: true,
              handler: () => setIsFiltering(false),
              handleBackButtonClick
            }}
          >
            <ProductFilterModal
              setCloseModal={setIsFiltering}
              updateCurrentUrl={updateCurrentUrl}
              priceRange={priceRange}
            />
          </FilterModal>
        )}
      </div>
    </div>
  );
};

const CollectionDetails = ({
  collectionSlug,
  initialCollectionName,
  priceRange
}) => {
  const router = useRouter();
  const [isSorting, setIsSorting] = useState(false);
  const [isMobile] = useIsMobile();
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentUrl, setCurrentUrl] = useState();
  const [collectionName, setCollectionName] = useState(initialCollectionName);
  const [openTab, setOpenTab] = useState(1);

  const getCollectionNameByCollectionSlug = async () => {
    const collection = await getCollectionBySlug(collectionSlug);
    setCollectionName(collection.entity.name);
  };

  useEffect(() => {
    if (!initialCollectionName) {
      getCollectionNameByCollectionSlug();
    }
  }, []);

  const searchClient = useMemo(
    () =>
      createClient({
        headers: () => ({
          'x-access-token': getAuthToken(),
          'x-origin-domain': HOST_PORTAL_FQDN
        }),
        url: searchAPIConfig.productListAPIEndPoint
      }),
    []
  );

  const updateCurrentUrl = () => setCurrentUrl(window.location.href);
  const handleBackButtonClick = () => {
    if (currentUrl) router.push(currentUrl);
  };
  const onSortClick = () => {
    setIsSorting(false);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <HeaderOrganism
        className='hidden md:flex'
        heading='Collections'
      />
      <InstantSearchNext
        indexName={INDEX_NAME}
        searchClient={searchClient}
        routing={true}
      >
        <Configure
          filters={`assignedToCollections:${collectionSlug}`}
          hitsPerPage={isMobile ? 50 : 15}
        />
        <CollectionDetailsContent
          collectionName={collectionName}
          isMobile={isMobile}
          isFiltering={isFiltering}
          setIsFiltering={setIsFiltering}
          isSorting={isSorting}
          setIsSorting={setIsSorting}
          openTab={openTab}
          setOpenTab={setOpenTab}
          priceRange={priceRange || defaultPriceRange}
          handleBackButtonClick={handleBackButtonClick}
          updateCurrentUrl={updateCurrentUrl}
          onSortClick={onSortClick}
        />
      </InstantSearchNext>
    </>
  );
};

export default CollectionDetails;
